import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useVenueProfile } from '@/contexts/VenueProfileContext';
import { useUserRole } from '@/hooks/useUserRole';
import { type AppRole, canAccessRoute } from '@/services/roleService';

interface RoleGuardProps {
  children: ReactNode;
  requiredRole?: AppRole;
  requireAdmin?: boolean;
  requireOwner?: boolean;
  requireBillingAccess?: boolean;
  requireGMAccess?: boolean;
}

export function RoleGuard({
  children,
  requiredRole: _requiredRole,
  requireAdmin,
  requireOwner,
  requireBillingAccess,
  requireGMAccess,
}: RoleGuardProps) {
  const location = useLocation();
  const { role, isAdmin, isLoading, canAccessBilling, canAccessGMReports } = useUserRole();
  const { isOwner, isLoading: isProfileLoading } = useVenueProfile();

  // Admin override via localStorage (for development, screenshots, demos)
  if (
    typeof window !== 'undefined' &&
    localStorage.getItem('venueintel-admin-override') === 'true'
  ) {
    return <>{children}</>;
  }

  if (isLoading || isProfileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check if owner is required (for venue setup, integrations)
  if (requireOwner && !isOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check specific requirements
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireBillingAccess && !canAccessBilling) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireGMAccess && !canAccessGMReports) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check route access
  if (role && !canAccessRoute(role, location.pathname)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
